import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';

interface TailwindDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl';
  showCloseButton?: boolean;
  variant?: 'light' | 'dark' | 'themed';
}


export const TailwindDialog: React.FC<TailwindDialogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '2xl',
  showCloseButton = true,
  variant = 'light'
}) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen) {
      dialog.showModal();
      // Empêcher le scroll du body
      document.body.style.overflow = 'hidden';
    } else {
      dialog.close();
      // Restaurer le scroll du body
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const requestClose = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      onClose();
    }, 200);
  }, [onClose]);

  // Gestion de la touche Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        requestClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, requestClose]);

  const maxWidthClasses = {
    sm: 'sm:max-w-sm',
    md: 'sm:max-w-md',
    lg: 'sm:max-w-lg',
    xl: 'sm:max-w-xl',
    '2xl': 'sm:max-w-2xl',
    '3xl': 'sm:max-w-3xl',
    '4xl': 'sm:max-w-4xl',
    '5xl': 'sm:max-w-5xl',
    '6xl': 'sm:max-w-6xl',
    '7xl': 'sm:max-w-7xl',
  };

  useEffect(() => {
    if (isOpen) {
      // Active l'animation d'entrée après le montage
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-0 p-0 w-full h-full bg-transparent backdrop:bg-black/60 backdrop-blur-sm"
      style={{ margin: 0, padding: 0, border: 'none' }}
    >

      {/* Dialog Container avec animation - Plein écran sur mobile */}
      <div className="fixed inset-0 sm:relative sm:flex sm:items-center sm:justify-center sm:p-3 md:p-4 lg:p-6">
        <div
          className={`transform overflow-hidden rounded-none sm:rounded-xl md:rounded-2xl ${variant === 'dark'
            ? 'bg-slate-900 text-white border border-slate-700'
            : variant === 'themed'
              ? 'bg-background text-foreground border border-border'
              : 'bg-white text-left'
            } shadow-xl transition-transform transition-opacity duration-200 ease-out w-full h-full sm:w-full sm:h-auto sm:max-h-[90vh] ${maxWidthClasses[maxWidth]} ${visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-3 scale-95'} sm:mx-auto flex flex-col`}
          style={{
            willChange: 'transform, opacity',
            transform: 'translateZ(0)',
            margin: 0,
            padding: 0
          }}
        >
          {/* Header moderne - seulement si showCloseButton est true - RESPONSIVE */}
          {showCloseButton && (
            <div className={`flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0 ${variant === 'dark'
              ? 'border-slate-800 bg-slate-900'
              : variant === 'themed'
                ? 'border-border bg-background-secondary'
                : 'border-gray-100 bg-gradient-to-r from-gray-50 to-white'
              }`}>
              <h3 className={`text-lg sm:text-xl font-bold ${variant === 'dark'
                ? 'text-white'
                : variant === 'themed'
                  ? 'text-foreground'
                  : 'text-gray-900'
                } truncate pr-2`}>
                {title}
              </h3>
              <button
                onClick={requestClose}
                className={`${variant === 'dark'
                  ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                  : variant === 'themed'
                    ? 'text-foreground-secondary hover:text-foreground hover:bg-background-tertiary'
                    : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  } transition-all duration-200 rounded-lg p-1.5 sm:p-2 flex-shrink-0`}
                aria-label="Fermer"
              >
                <X className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          )}

          {/* Content : flex column sans scroll - les enfants gèrent leur propre scroll + footer fixe */}
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {children}
          </div>
        </div>
      </div>
    </dialog>
  );
};


// Composant pour les boutons d'action du dialog
interface DialogActionsProps {
  children: React.ReactNode;
  className?: string;
}

export const DialogActions: React.FC<DialogActionsProps> = ({
  children,
  className = "flex justify-end gap-3 p-6 border-t border-border bg-background-secondary"
}) => (
  <div className={className}>
    {children}
  </div>
);

// Composant pour le contenu principal du dialog
interface DialogContentProps {
  children: React.ReactNode;
  className?: string;
}

export const DialogContent: React.FC<DialogContentProps> = ({
  children,
  className = "p-6"
}) => (
  <div className={className}>
    {children}
  </div>
);
